<template>
  <div class="flex flex-col w-full">
    <div class="flex flex-col gap-[18px]">
      <div :class="['overflow-x-scroll', width === '100%' ? '' : 'table_wrapper']">
        <div class="custom_table" :style="customTableStyle">
          <div class="table_container" :style="`width: ${width}`">
            <div class="flex w-full">
              <div :class="['grid table_head']" :style="getLeftGridStyle">
                <div v-if="showCheckboxes" class="col-span-1 table_header flex justify-center items-center border_right">
                  <a-checkbox :checked="isAllPageSelected || isAllPageSelectedEntity" :indeterminate="isIndeterminate || isIndeterminateEntity" @change="onSelectAllChange" />
                </div>
                <div
                  v-for="(header, index) in leftHeaders"
                  :key="index"
                  :class="[`col-span-${header.width}`, { border_right: addSideBorder }, { 'cursor-pointer': showSort }]"
                  class="table_header"
                  @click="handleHeaderClick(header)"
                >
                  <HeaderLabel :label="header.label" :info-icon="header.infoIcon" :tooltip="header.tooltip" />
                  <div v-if="showSort" :class="[Object.values(currentSort)?.some((item) => item.field === header.key) ? 'sorting_applied' : '', 'flex flex-col gap-1']">
                    <img class="w-[6px] h-1 cursor-pointer" :src="getSortIcon(header.key, 'asc')" alt="sort_up_arrow" @click.stop="toggleSort(header.key, 'asc')" />
                    <img class="w-[6px] h-1 cursor-pointer rotate-180" :src="getSortIcon(header.key, 'desc')" alt="sort_down_arrow" @click.stop="toggleSort(header.key, 'desc')" />
                  </div>
                </div>
              </div>

              <div v-if="showActionsColumn && actionPosition" class="action_column_header border_right" :style="{ minWidth: actionHeaderCSS }">
                {{ quickActions?.length > 0 && showActionsHeader ? 'Actions' : '' }}
              </div>

              <div v-if="rightHeaders.length > 0" :class="['grid table_head']" :style="getRightGridStyle">
                <div
                  v-for="(header, index) in rightHeaders"
                  :key="index"
                  :class="[`col-span-${header.width}`, { border_right: addSideBorder }, { 'cursor-pointer': showSort }]"
                  class="table_header"
                  @click="handleHeaderClick(header)"
                >
                  <HeaderLabel :label="header.label" :info-icon="header.infoIcon" :tooltip="header.tooltip" />
                  <div v-if="showSort" :class="[Object.values(currentSort)?.some((item) => item.field === header.key) ? 'sorting_applied' : '', 'flex flex-col gap-1']">
                    <img class="w-[6px] h-1 cursor-pointer" :src="getSortIcon(header.key, 'asc')" alt="sort_up_arrow" @click.stop="toggleSort(header.key, 'asc')" />
                    <img class="w-[6px] h-1 cursor-pointer rotate-180" :src="getSortIcon(header.key, 'desc')" alt="sort_down_arrow" @click.stop="toggleSort(header.key, 'desc')" />
                  </div>
                </div>
              </div>

              <div v-if="showActionsColumn && !actionPosition" class="action_column_header" :style="{ minWidth: actionHeaderCSS }">
                {{ quickActions?.length > 0 && showActionsHeader ? 'Actions' : '' }}
              </div>
            </div>

            <div
              class="table_body"
              :style="{
                maxHeight: width === '100%' ? height : `calc(${height} - 10px)`,
              }"
            >
              <div v-for="(row, rowIndex) in data" :key="rowIndex" :class="[`flex w-full`]">
                <div :class="['grid table_row', { 'cursor-pointer': customRow }]" :style="getLeftGridStyle">
                  <div v-if="showCheckboxes" class="col-span-1 flex justify-center items-center border_right">
                    <a-checkbox :checked="isSelected(row) || isEntitySelected(row)" @change="(e) => onSelectChange(e, row)" />
                  </div>
                  <template v-if="customRow">
                    <a :href="customRow(row)?.['data-href']" class="contents" @click.prevent>
                      <TableCell
                        v-for="(header, colIndex) in leftHeaders"
                        :key="colIndex"
                        :header="header"
                        :row="row"
                        :col-index="colIndex"
                        :source="source"
                        :custom-column-render="customColumnRender"
                        :empty-cell-value="emptyCellValue"
                        :add-side-border="addSideBorder"
                        :custom-row="customRow"
                        :suffix-key="suffixKey"
                        :suffix-component="suffixComponent"
                        :description-key="descriptionKey"
                        :extra-data="extraData"
                        @update="$emit('update', $event)"
                        @refresh="$emit('refresh', $event)"
                      />
                    </a>
                  </template>
                  <template v-else>
                    <TableCell
                      v-for="(header, colIndex) in leftHeaders"
                      :key="colIndex"
                      :header="header"
                      :row="row"
                      :col-index="colIndex"
                      :source="source"
                      :custom-column-render="customColumnRender"
                      :empty-cell-value="emptyCellValue"
                      :add-side-border="addSideBorder"
                      :custom-row="customRow"
                      :suffix-key="suffixKey"
                      :suffix-component="suffixComponent"
                      :description-key="descriptionKey"
                      :extra-data="extraData"
                      @update="$emit('update', $event)"
                      @refresh="$emit('refresh', $event)"
                    />
                  </template>
                </div>

                <TableActions
                  v-if="showActionsColumn && actionPosition"
                  :row="row"
                  :row-index="rowIndex"
                  :actions="actions"
                  :quick-actions="quickActions"
                  :source="source"
                  :column-width="actionColumnCSS"
                  :is-last-column="false"
                  :dropdown-index="dropdownIndex"
                  :from-prospect="fromProspect"
                  :dropdown-position="dropdownPosition"
                  :custom-action-key="customActionKey"
                  :custom-action-component="customActionComponent"
                  @action="performAction"
                  :prospect-data="prospectData"
                  :is-more-options-disabled="isMoreOptionsDisabled"
                />

                <div v-if="rightHeaders.length > 0" :class="['grid table_row', { 'cursor-pointer': customRow }]" :style="getRightGridStyle">
                  <TableCell
                    v-for="(header, colIndex) in rightHeaders"
                    :key="colIndex"
                    :header="header"
                    :row="row"
                    :col-index="colIndex"
                    :source="source"
                    :custom-column-render="customColumnRender"
                    :empty-cell-value="emptyCellValue"
                    :add-side-border="addSideBorder"
                    :custom-row="customRow"
                    :suffix-key="suffixKey"
                    :suffix-component="suffixComponent"
                    :description-key="descriptionKey"
                    :extra-data="extraData"
                    @update="$emit('update', $event)"
                    @refresh="$emit('refresh', $event)"
                  />
                </div>

                <TableActions
                  v-if="showActionsColumn && !actionPosition"
                  :row="row"
                  :row-index="rowIndex"
                  :actions="actions"
                  :quick-actions="quickActions"
                  :source="source"
                  :column-width="actionColumnCSS"
                  :is-last-column="true"
                  :from-prospect="fromProspect"
                  :dropdown-index="dropdownIndex"
                  :dropdown-position="dropdownPosition"
                  :custom-action-key="customActionKey"
                  :custom-action-component="customActionComponent"
                  @action="performAction"
                  :prospect-data="prospectData"
                  :is-more-options-disabled="isMoreOptionsDisabled"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <PaginationHandler v-if="showPagination" :pagination="pagination" :allow-change-page-size="showPageSize" @update:pagination="handlePaginationUpdate" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import imagePath from '@/javascript/imagePath';
import PaginationHandler from '@/components/custom/PaginationHandler.vue';
import TableActions from './TableActions.vue';
import TableCell from './TableCell.vue';
import HeaderLabel from '@/components/custom/HeaderLabel.vue';

const props = defineProps({
  source: { type: String, default: '' },
  headers: Object,
  data: Object,
  pagination: { type: Object, default: () => ({}), required: false },
  showActionsColumn: { type: Boolean, default: false },
  showSelectionInfo: { type: Boolean, default: true },
  showPagination: { type: Boolean, default: true },
  actions: { type: Array, default: () => [] },
  quickActions: { type: Array, default: () => [], required: false },
  customRow: { type: Function },
  width: { type: String, default: '100%' },
  height: { type: String, default: '72.8vh' },
  addSideBorder: { type: Boolean, default: false },
  customColumnRender: { type: Object, default: () => ({}) },
  initialSort: { type: Object, default: () => ({}) },
  showSort: { type: Boolean, default: false },
  toShowActions: { type: [Function, Boolean], default: false },
  prefixImageBorderRadius: { type: String, default: '50%' },
  showCheckboxes: { type: Boolean, default: false },
  isMoreOptionsDisabled: { type: String, required: false },
  showActionsHeader: { type: Boolean, required: false, default: true },
  selection: {
    type: Object,
    default: () => ({
      selected_ids: [],
      unselected_ids: [],
      select_all: false,
    }),
  },
  selectedEntities: {
    type: Object,
    default: () => ({
      selected_entities: [],
      unselected_entities: [],
      select_all: false,
    }),
  },
  identifierKey: { type: String, default: 'id' },
  tableType: { type: String, default: '' },
  listId: { type: Number, required: false },
  suffixKey: { type: String, required: false },
  suffixComponent: { type: Object, required: false },
  descriptionKey: { type: String, required: false },
  totalColumns: { type: Number, default: 12 },
  showPageSize: { type: Boolean, default: false },
  emptyCellValue: { type: String, default: '' },
  viewId: { type: Number, default: null },
  actionPosition: { type: Number, default: null },
  extraData: { type: Object, required: false, default: null },
  customActionKey: { type: String, required: false },
  fromProspect: { type: Boolean, required: false, default: false },
  customActionComponent: { type: Object, default: null },
  prospectData: { type: Boolean, default: false, required: false },
  customActionWidth: { type: Number, default: 52 },
});

const emit = defineEmits(['update:pagination', 'update:sort', 'update:selection', 'update:selected-entities', 'update:listRemove', 'update:ownerAssigned', 'update', 'refresh']);

const currentSort = ref(props.initialSort || {});
const dropdownIndex = ref(null);
const dropdownPosition = ref({ top: 0, left: 0 });

const leftHeaders = computed(() => {
  if (!props.actionPosition) return props.headers;
  return props.headers.slice(0, props.actionPosition);
});

const rightHeaders = computed(() => {
  if (!props.actionPosition) return [];
  return props.headers.slice(props.actionPosition);
});

const leftGridWidth = computed(() => {
  if (!props.actionPosition) return props.width;
  const totalLeftColumns = leftHeaders.value.reduce((acc, header) => acc + header.width, 0);
  const totalColumns = props.headers.reduce((acc, header) => acc + header.width, 0);
  return `${(totalLeftColumns / totalColumns) * 100}%`;
});

const rightGridWidth = computed(() => {
  if (!props.actionPosition) return '0';
  const totalRightColumns = rightHeaders.value.reduce((acc, header) => acc + header.width, 0);
  const totalColumns = props.headers.reduce((acc, header) => acc + header.width, 0);
  return `${(totalRightColumns / totalColumns) * 100}%`;
});

const getLeftGridStyle = computed(() => {
  const totalCols = leftHeaders.value.reduce((acc, header) => acc + header.width, 0);
  return {
    gridTemplateColumns: `${props.showCheckboxes ? '40px ' : ''}repeat(${totalCols}, minmax(0, 1fr))`,
    width: leftGridWidth.value,
  };
});

const getRightGridStyle = computed(() => {
  const totalCols = rightHeaders.value.reduce((acc, header) => acc + header.width, 0);
  return {
    gridTemplateColumns: `repeat(${totalCols}, minmax(0, 1fr))`,
    width: rightGridWidth.value,
  };
});

const customTableStyle = computed(() => {
  if (String(props.width).trim() === '100%') {
    return {
      border: '1px solid rgba(255, 255, 255, 0.12)',
      borderRadius: '8px',
    };
  } else {
    return {
      borderTopRightRadius: '8px',
      borderTopLeftRadius: '8px',
    };
  }
});

const actionColumnCSS = computed(() => {
  const customActionComponentWidth = props.customActionComponent ? props.customActionWidth : 0;
  const quickActionWidth = 32;
  const moreActionWidth = 32;
  const padding = 16;
  const gap = 4;

  const totalQuickActionsWidth = props.quickActions?.length * (quickActionWidth + gap);
  const moreActionsWidth = props.actions?.length > 0 ? moreActionWidth : 0;

  const totalWidth = totalQuickActionsWidth + moreActionsWidth + customActionComponentWidth + padding;
  return `${totalWidth}px`;
});

const actionHeaderCSS = computed(() => {
  return actionColumnCSS.value;
});

const isAllPageSelected = computed(() => {
  return props.data.every((row) => isSelected(row));
});

const isIndeterminate = computed(() => {
  const selectedCount = props.data.filter((row) => isSelected(row)).length;
  return selectedCount > 0 && selectedCount < props.data.length;
});

const handlePaginationUpdate = (newPagination) => {
  emit('update:pagination', newPagination);
};

const performAction = ({ action, row }) => {
  if (action.handler && typeof action.handler === 'function') {
    action.handler(row);
  }
};

const isSelected = (item) => {
  const id = item[props.identifierKey];
  if (props.selection.select_all) {
    return !props.selection.unselected_ids.includes(id);
  }
  return props.selection.selected_ids.includes(id);
};

const isEntitySelected = (item) => {
  const entity = {
    id: item[props.identifierKey],
    entity_type: item.entity_type,
  };

  const serializeEntity = (entity) => `${entity.id}:${entity.entity_type}`;
  const serializedEntity = serializeEntity(entity);

  if (props.selectedEntities.select_all) {
    return !props.selectedEntities.unselected_entities.map(serializeEntity).includes(serializedEntity);
  }
  return props.selectedEntities.selected_entities.map(serializeEntity).includes(serializedEntity);
};

const isAllPageSelectedEntity = computed(() => {
  return props.data.every((row) => isEntitySelected(row));
});

const isIndeterminateEntity = computed(() => {
  const selectedCount = props.data.filter((row) => isEntitySelected(row)).length;
  return selectedCount > 0 && selectedCount < props.data.length;
});

const onSelectAllChange = (e) => {
  const ids = props.data.map((row) => row[props.identifierKey]);
  updateSelection(ids, e.target.checked);

  const entities = props.data.map((row) => ({
    id: row[props.identifierKey],
    entity_type: row.entity_type,
  }));
  updateEntitySelection(entities, e.target.checked);
};

const onSelectChange = (e, item) => {
  updateSelection([item[props.identifierKey]], e.target.checked);
  const entity = {
    id: item[props.identifierKey],
    entity_type: item.entity_type,
  };
  updateEntitySelection([entity], e.target.checked);
};

const updateSelection = (ids, isSelected) => {
  let newSelection;
  if (props.selection.select_all) {
    if (isSelected) {
      newSelection = {
        ...props.selection,
        unselected_ids: props.selection.unselected_ids.filter((id) => !ids.includes(id)),
      };
    } else {
      newSelection = {
        ...props.selection,
        unselected_ids: [...new Set([...props.selection.unselected_ids, ...ids])],
      };
    }
  } else {
    if (isSelected) {
      newSelection = {
        ...props.selection,
        selected_ids: [...new Set([...props.selection.selected_ids, ...ids])],
      };
    } else {
      newSelection = {
        ...props.selection,
        selected_ids: props.selection.selected_ids.filter((id) => !ids.includes(id)),
      };
    }
  }
  emit('update:selection', newSelection);
};

const updateEntitySelection = (entities, isSelected) => {
  const serializeEntity = (entity) => `${entity.id}:${entity.entity_type}`;

  let newSelection;

  if (props.selectedEntities.select_all) {
    if (isSelected) {
      const unselectedMap = new Map(props.selectedEntities.unselected_entities.map((entity) => [serializeEntity(entity), entity]));
      entities.forEach((entity) => {
        unselectedMap.delete(serializeEntity(entity));
      });

      newSelection = {
        ...props.selectedEntities,
        unselected_entities: Array.from(unselectedMap.values()),
      };
    } else {
      const unselectedMap = new Map([...props.selectedEntities.unselected_entities, ...entities].map((entity) => [serializeEntity(entity), entity]));
      newSelection = {
        ...props.selectedEntities,
        unselected_entities: Array.from(unselectedMap.values()),
      };
    }
  } else {
    if (isSelected) {
      const selectedMap = new Map([...props.selectedEntities.selected_entities, ...entities].map((entity) => [serializeEntity(entity), entity]));
      newSelection = {
        ...props.selectedEntities,
        selected_entities: Array.from(selectedMap.values()),
      };
    } else {
      const entitiesToRemove = new Set(entities.map(serializeEntity));
      newSelection = {
        ...props.selectedEntities,
        selected_entities: props.selectedEntities.selected_entities.filter((entity) => !entitiesToRemove.has(serializeEntity(entity))),
      };
    }
  }
  emit('update:selected-entities', newSelection);
};

const toggleSort = (field, order) => {
  const newSort = { 1: { field, order } };
  currentSort.value = newSort;
  emit('update:sort', newSort);
};

const handleHeaderClick = (header) => {
  if (!props.showSort) return;
  const sort = Object.values(currentSort.value).find((item) => item.field === header.key);
  if (sort) {
    if (sort.order === 'desc') {
      toggleSort(sort.field, 'asc');
    } else {
      const newSort = {};
      currentSort.value = newSort;
      emit('update:sort', newSort);
    }
  } else {
    toggleSort(header.key, 'desc');
  }
};

const handleClickOutside = (event) => {
  if (!event.target.closest('.action_wrapper')) {
    dropdownIndex.value = null;
  }
};

const getSortIcon = (field, order) => {
  return Object.values(currentSort.value)?.some((item) => item.field === field && item.field === field && item.order === order) ? imagePath.sortingArrowSelected : imagePath.sortingArrow;
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style>
.custom_table {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  color: var(--foreground-rgb);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  min-width: min-content;
  overflow: hidden;
}

.table_container {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow-x: scroll;
  overflow-y: hidden;
  border: 1px solid var(--white-opacity-20);
  border-radius: 8px;
}

.table_head,
.table_row {
  display: grid;
  align-items: stretch;
}

.table_head {
  background-color: var(--white-opacity-8);
}

.table_body {
  flex: 1;
  background: transparent;
  overflow-y: scroll;
  overflow-x: hidden;
}

.table_header {
  display: flex;
  padding: 8px 12px;
  align-items: center;
  justify-content: space-between;
  gap: 4px;
  overflow: hidden;
  color: var(--secondary-color);
  text-overflow: ellipsis;
  font-family: 'Proxima Nova', sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
}

.table_data,
.action_wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 8px;
  align-self: stretch;
  overflow: hidden;
  color: var(--foreground-rgb);
  text-overflow: ellipsis;
  font-family: 'Proxima Nova', sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
}

.action_wrapper {
  justify-content: flex-end;
}

.action_column_header {
  padding: 8px 12px;
  background-color: var(--white-opacity-8);
  color: var(--secondary-color);
  font-family: 'Proxima Nova', sans-serif;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  box-sizing: border-box;
}

.action_column {
  background-color: transparent;
  display: flex;
  flex-direction: column;
}

.table_row {
  border-bottom: 1px solid var(--white-opacity-20);
}

@media (max-width: 768px) {
  .table_container {
    overflow-x: auto;
  }
  .table_data {
    flex-direction: column;
    align-items: flex-start;
  }
}

.sorting_applied {
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 6px;
  background: var(--white-opacity-8);
  width: 16px;
  height: 18px;
}

/* Webkit browsers (Chrome, Safari, newer versions of Edge) */
.table_wrapper::-webkit-scrollbar {
  width: 8px;
}

.table_wrapper::-webkit-scrollbar-track {
  background: transparent;
}

.table_wrapper::-webkit-scrollbar-thumb {
  background-color: var(--white-opacity-20);
  border-radius: 4px;
}

/* For Firefox */
.table_wrapper {
  scrollbar-width: thin;
  scrollbar-color: var(--white-opacity-20) transparent;
  border: 1px solid var(--white-opacity-20);
  border-radius: 8px;
}

/* For IE and Edge */
.table_wrapper {
  -ms-overflow-style: none;
}

.table_wrapper::-ms-scrollbar {
  width: 8px;
}

.table_wrapper::-ms-scrollbar-track {
  background: transparent;
}

.table_wrapper::-ms-scrollbar-thumb {
  background-color: var(--white-opacity-20);
  border-radius: 4px;
}
</style>
